import React, {useContext} from "react";
import {useIntl} from "react-intl";
import {IconButton} from "@spg/component_library";
import API from "../logic/API";
import {Connector, TopicDescriptionInfo} from "../logic/APITypes";
import {SnackbarContext} from "./SnackbarProvider";
import play from "../images/play.svg";
import pause from "../images/pause.svg";

type ToggleStatusButtonProps<T> = {
  /** The topic/connector augmented by a flag whether it's toggling state at the moment. */
  topic: T;
  /** Function to update the topic/connector and the isToggling-flag. */
  setTopic: (topic: T) => void;
  /** Size of the button, defaults to 48px * 24px */
  buttonSize?: {height: number; width: number};
};

/**
 * A play/pause-button to activate/deactivate topics or connectors.
 */
function ToggleStatusButton<
  T extends (Connector | TopicDescriptionInfo) & {isToggling: boolean},
>(props: ToggleStatusButtonProps<T>) {
  const intl = useIntl();
  const addSnackbar = useContext(SnackbarContext).addSnackbar;

  return (
    <IconButton
      idBase={"toggleStatus_" + props.topic.id + "_"}
      onClick={() => {
        props.setTopic({...props.topic, isToggling: true});
        API.updateStatus(
          props.topic.id,
          props.topic.health.startsWith("ACTIVE") ? "STOP" : "START",
        ).then((response) => {
          const newTopic = {
            ...props.topic,
            isToggling: false,
          };
          if (response.success && response.payload) {
            newTopic["health"] = response.payload?.health;
          }
          props.setTopic(newTopic);
          if (!response.success)
            addSnackbar({
              variant: "close",
              hasCloseButton: true,
              text: intl.formatMessage({
                id: `toggleStatusButton.snackbar.${
                  "description" in props.topic ? "topic" : "connector"
                }.${
                  props.topic.health.startsWith("ACTIVE")
                    ? "deactivating"
                    : "activating"
                }.failed`,
              }),
            });
        });
      }}
      disabled={props.topic.isToggling}
      buttonSize={props.buttonSize || {height: 24, width: 48}}
      iconProps={{
        href:
          (props.topic.health.startsWith("ACTIVE") ? pause : play) + "#globalG",
        strokeColor: props.topic.isToggling ? "var(--colorBase300)" : "#FF3D00",
        title: intl.formatMessage({
          id: `toggleStatusButton.status.${
            props.topic.health.startsWith("ACTIVE") ? "deactivate" : "activate"
          }`,
        }),
      }}
    />
  );
}

export {ToggleStatusButton};
